<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, ref, type Slots, useAttrs, useId, useSlots, useTemplateRef, watch } from 'vue'
import { autoUpdate, flip, offset, type Placement, shift, size as sizeMiddleware, useFloating } from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import { useBreakpoint } from '@composables/useBreakpoint'
import { useParentFocusTrap } from '@ui/composables/useOverlayFocusTrap'
import { useUiKitMessages } from '@ui/composables/useUiKitMessages'

import { overlayLayerTarget } from '@/utils/overlayLayers'

import AppSelectItem from './components/AppSelectItem.vue'
import AppIcon from '@ui/app-icon/AppIcon.vue'
import AppInput from '@ui/app-input/AppInput.vue'
import AppModal from '@ui/app-modal/AppModal.vue'

import type { AppSelectEmits, AppSelectModelValue, AppSelectProps, SelectItem } from './types'

const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, [role="button"], [role="checkbox"], [tabindex]:not([tabindex="-1"])'
const CLICK_ON_KEY_TARGETS = 'button, a[href], select, textarea, input:not([type="checkbox"]):not([type="radio"]), [role="button"]'
const TEXT_FIELD_TARGETS = 'textarea, input:not([type="checkbox"]):not([type="radio"])'
const LISTBOX_MODES: string[] = ['single', 'multiple', 'event']

interface OpenOptions {
  viaKeyboard?: boolean
  fromEnd?: boolean
}

interface CloseOptions {
  restoreFocus?: boolean
}

const props = withDefaults(defineProps<AppSelectProps>(), {
  items: () => [],
  placeholder: '',
  size: 'md',
  variant: 'grey',
  disabled: false,
  error: false,
  description: '',
  label: '',
  filter: false,
  filterPlaceholder: '',
  divider: false,
  prefix: '',
  mode: 'single',
  uniqueSlots: false,
  block: false,
  dropdownWidth: undefined,
  hideMode: 'if',
  emptyText: '',
  messages: () => ({}),
  loading: false,
  fullScreenMobile: false,
  placement: 'bottom-start',
})

const attrs = useAttrs()
const slots: Slots = useSlots()
const model = defineModel<AppSelectModelValue>({
  default: null,
})

const emit = defineEmits<AppSelectEmits>()

const instance = getCurrentInstance()

const selectId = useId()
const isOpen = ref(false)
const filterValue = ref('')

watch(filterValue, (val) => { if (isOpen.value) emit('filter', val) })

const activeIndex = ref(-1)

const selectRef = ref<HTMLDivElement | null>(null)
const triggerRef = useTemplateRef<HTMLDivElement>('triggerRef')
const filterRef = useTemplateRef<InstanceType<typeof AppInput>>('filterRef')
const dropdownRef = useTemplateRef<HTMLDivElement>('dropdownRef')
const defaultInputRef = useTemplateRef<InstanceType<typeof AppInput>>('defaultInputRef')
const floatingRef = useTemplateRef<HTMLDivElement>('floatingRef')
const teleportPlace = ref('body')
const floatingTarget = overlayLayerTarget('dropdowns')

const text = useUiKitMessages('select', () => props.messages)
const { breakpointsMap, breakpoints } = useBreakpoint()
const parentFocusTrap = useParentFocusTrap()
const { floatingStyles } = useFloating(triggerRef, floatingRef, {
  strategy: 'fixed',
  placement: () => props.placement.replace(/-center$/, '') as Placement,
  middleware: [
    offset(() => {
      const wrapperEl = triggerRef.value
      const inputEl = defaultInputRef.value?.elementRef
      if (!wrapperEl || !inputEl) return 2
      const belowInputHeight = wrapperEl.getBoundingClientRect().bottom - inputEl.getBoundingClientRect().bottom
      return 2 - belowInputHeight
    }),
    flip(),
    shift(),
    sizeMiddleware({
      apply({ availableHeight, elements }) {
        const maxHeight = Math.min(400, availableHeight - 16)
        const propMaxWidth = props.dropdownWidth != null
          ? /^\d+(\.\d+)?$/.test(String(props.dropdownWidth))
            ? `${props.dropdownWidth}px`
            : props.dropdownWidth
          : null
        const maxWidth = propMaxWidth || `${(elements.reference as HTMLElement).scrollWidth}px`

        Object.assign(elements.floating.style, {
          width: '100%',
          maxWidth,
          maxHeight: `${maxHeight}px`,
        })
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
})

const isOpenModal = computed({
  get() {
    return isOpen.value
  },
  set(value) {
    value ? openSelect() : closeSelect()
  },
})

const isListbox = computed<boolean>(() => LISTBOX_MODES.includes(props.mode) && !slots.items)

const isRenderedItems = computed(() => {
  if (props.hideMode === 'if') {
    return isOpen.value
  }
  return true
})

const resolvedEmptyText = computed(() => props.emptyText || text.value.empty)

const filteredItems = computed(() => {
  if (!props.filter || !filterValue.value) return props.items
  if (instance?.vnode.props?.onFilter) return props.items
  const lowerCasedFilterValue = filterValue.value.toLowerCase()
  return props.items.filter(item =>
    item.text.toLowerCase().includes(lowerCasedFilterValue),
  )
})

const selectedValue = computed(() => {
  if (props.mode === 'multiple') {
    const selected = Array.isArray(model.value) ? model.value : []
    const selectedItems = selected.reduce<string[]>((acc, id) => {
      const item = props.items.find(item => item.value === id)
      if (item) acc.push(item.text)
      return acc
    }, [])

    const maxItems = isMobile.value ? 1 : 3
    return selectedItems.slice(0, maxItems).join(', ') + (selectedItems.length > maxItems ? `, (+${selectedItems.length - maxItems})` : '')
  } else {
    return props.items.find(item => item.value === model.value)?.text
  }
})

const isMobile = computed(() => {
  return breakpoints.smaller(breakpointsMap.tablet).value
})

const enabledIndexes = computed(() =>
  filteredItems.value.reduce<number[]>((acc, item, index) => {
    if (!item.disabled) acc.push(index)
    return acc
  }, []),
)

const optionElements = () => Array.from(
  dropdownRef.value?.querySelectorAll<HTMLElement>(':scope > [data-test-id="app-select-option"]') || [],
)

const focusOption = (index: number) => {
  const el = optionElements()[index]
  if (!el) return
  activeIndex.value = index
  ;(el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) || el).focus?.()
  el.scrollIntoView?.({ block: 'nearest' })
}

const focusTrigger = () => {
  const el = defaultInputRef.value?.elementRef ||
    triggerRef.value?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ||
    triggerRef.value
  el?.focus?.()
}

const focusFirstTarget = (fromEnd = false) => {
  if (props.filter) {
    filterRef.value?.elementRef?.focus?.()
    return
  }
  const indexes = enabledIndexes.value
  if (!indexes.length) return
  focusOption(fromEnd ? indexes.at(-1)! : indexes[0])
}

const moveActive = (step: number) => {
  const indexes = enabledIndexes.value
  if (!indexes.length) return
  const current = indexes.indexOf(activeIndex.value)
  const next = current === -1
    ? (step > 0 ? 0 : indexes.length - 1)
    : Math.min(Math.max(current + step, 0), indexes.length - 1)
  focusOption(indexes[next])
}

const openSelect = async(options?: OpenOptions) => {
  if (props.disabled) return
  if (!isMobile.value) parentFocusTrap.pause()
  isOpen.value = true
  emit('open')
  await nextTick()
  teleportPlace.value = `#${selectId}`
  if (options?.viaKeyboard !== true) return
  await nextTick()
  focusFirstTarget(options.fromEnd)
}

const closeSelect = (options?: CloseOptions) => {
  if (!isOpen.value) return
  teleportPlace.value = 'body'
  isOpen.value = false
  activeIndex.value = -1
  const hadFilter = !!filterValue.value
  filterValue.value = ''
  if (hadFilter) emit('filter', '')
  if (options?.restoreFocus === true) focusTrigger()
  if (!isMobile.value) parentFocusTrap.resume()
}

const toggleSelect = () => {
  if (props.disabled) return
  isOpen.value ? closeSelect() : openSelect()
}

const handleSelectItem = (item: SelectItem, options?: CloseOptions) => {
  if (item.disabled || props.disabled) return

  const restoreFocus = options?.restoreFocus === true

  const modeMap: Record<typeof props.mode, () => void> = {
    single: () => {
      model.value = item.value
      closeSelect({ restoreFocus })
    },
    multiple: () => {
      const current = Array.isArray(model.value) ? model.value : []
      model.value = current.includes(item.value)
        ? current.filter(value => value !== item.value)
        : [...current, item.value]
    },
    event: () => {
      closeSelect({ restoreFocus })
    },
    popover: () => {},
  }

  modeMap[props.mode]?.()

  emit('change', item.value)
}

const handleRowClick = (item: SelectItem, index: number, event?: MouseEvent) => {
  activeIndex.value = index
  handleSelectItem(item, { restoreFocus: event?.detail === 0 })
}

const checkIfSelected = (item: SelectItem) => {
  if (['event', 'popover'].includes(props.mode)) return false
  if (props.mode === 'multiple') {
    return Array.isArray(model.value) && model.value.includes(item.value)
  }
  return model.value === item.value
}

const handleTriggerKeydown = (e: KeyboardEvent) => {
  if (props.disabled) return

  if (e.key === 'Escape') {
    if (!isOpen.value) return
    e.preventDefault()
    closeSelect({ restoreFocus: true })
    return
  }

  if (!['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) return

  const isArrow = e.key === 'ArrowDown' || e.key === 'ArrowUp'
  const nativeTarget = (e.target as Element).closest?.(CLICK_ON_KEY_TARGETS)
  if (!isArrow && nativeTarget && nativeTarget !== defaultInputRef.value?.elementRef) return

  e.preventDefault()

  if (!isOpen.value) {
    openSelect({ viaKeyboard: true, fromEnd: e.key === 'ArrowUp' })
    return
  }

  isArrow ? focusFirstTarget(e.key === 'ArrowUp') : closeSelect({ restoreFocus: true })
}

const handleListKeydown = (e: KeyboardEvent) => {
  const target = e.target as Element

  if (e.key === 'Escape') {
    e.preventDefault()
    closeSelect({ restoreFocus: true })
    return
  }

  if (!isListbox.value) return

  if (e.key === 'Tab') {
    focusTrigger()
    closeSelect()
    if (!isMobile.value && parentFocusTrap.wrapFocus(e.shiftKey)) e.preventDefault()
    return
  }

  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    moveActive(e.key === 'ArrowDown' ? 1 : -1)
    return
  }

  if (e.key === 'Home' || e.key === 'End') {
    if (target.closest?.(TEXT_FIELD_TARGETS)) return
    const indexes = enabledIndexes.value
    if (!indexes.length) return
    e.preventDefault()
    focusOption(e.key === 'Home' ? indexes[0] : indexes.at(-1)!)
    return
  }

  if (!['Enter', ' '].includes(e.key)) return

  const optionEl = target.closest?.<HTMLElement>('[data-test-id="app-select-option"]')
  if (!optionEl || (target !== optionEl && target.closest(CLICK_ON_KEY_TARGETS))) return

  const item = filteredItems.value[optionElements().indexOf(optionEl)]
  if (!item) return

  e.preventDefault()
  handleSelectItem(item, { restoreFocus: true })
}

watch(filteredItems, () => {
  activeIndex.value = -1
})

onClickOutside(selectRef, () => closeSelect(), {
  ignore: ['.app-select-mobile-items', '.app-select-items'],
})

watch(isMobile, () => closeSelect())

defineExpose({
  open: openSelect,
  close: closeSelect,
  toggle: toggleSelect,
  isOpen,
})
</script>

<template>
  <div
    ref="selectRef"
    class="app-select"
    :class="{
      block: props.block
    }"
    v-bind="attrs"
  >
    <div
      ref="triggerRef"
      class="app-select-trigger-wrapper"
      :class="{
        block: props.block
      }"
      data-test-id="app-select-trigger"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-haspopup="isListbox ? 'listbox' : undefined"
      @click="toggleSelect"
      @keydown="handleTriggerKeydown"
    >
      <slot
        name="trigger"
        :is-open="isOpen"
        :open="openSelect"
        :close="closeSelect"
        :toggle="toggleSelect"
      >
        <div class="app-select-trigger" :class="{ block: props.block }">
          <app-input
            ref="defaultInputRef"
            class="app-select-input"
            :class="{
              open: isOpen,
            }"
            :description="props.description"
            :disabled="props.disabled"
            :error="props.error"
            :label="props.label"
            :model-value="selectedValue"
            :placeholder="props.placeholder"
            :size="props.size"
            :variant="props.variant"
            :prefix="props.prefix"
            :block="props.block"
            readonly
            hover
          >
            <template #right-icon>
              <app-icon
                :class="{
                  'open': isOpen,
                }"
                class="app-select-right-icon"
                name="ChevronDown"
              />
            </template>
          </app-input>
        </div>
      </slot>
    </div>

    <teleport :to="floatingTarget">
      <div
        v-if="!isMobile && isOpen"
        ref="floatingRef"
        :style="floatingStyles"
        class="app-select-items"
        @click.stop
      >
        <div :id="selectId" />
      </div>
    </teleport>

    <app-modal
      v-if="isMobile"
      v-model="isOpenModal"
      :title="placeholder"
      modal-class="app-select-mobile-items"
      layer="dropdowns"
      :full-height="fullScreenMobile"
    >
      <template #header>
        <slot name="mobile-placeholder" />
      </template>
      <template #body>
        <div :id="selectId" />
      </template>
    </app-modal>
  </div>

  <teleport
    :to="teleportPlace"
    :disabled="!isOpen"
  >
    <div
      v-if="isRenderedItems"
      v-show="isOpen"
      ref="dropdownRef"
      data-test-id="app-select-dropdown"
      :role="isListbox ? 'listbox' : undefined"
      :aria-multiselectable="isListbox && props.mode === 'multiple' ? 'true' : undefined"
      @keydown="handleListKeydown"
    >
      <app-input
        v-if="filter"
        ref="filterRef"
        v-model="filterValue"
        :placeholder="props.filterPlaceholder"
        data-test-id="app-select-filter"
        class="app-select-filter"
        :class="{
          mobile: isMobile
        }"
        left-icon="MagnifyingGlass"
      />

      <slot
        name="items"
        :close="closeSelect"
        :open="openSelect"
        :is-open="isOpen"
      >
        <template v-for="(item, index) in filteredItems" :key="item.value">
          <div
            class="app-select-option"
            data-test-id="app-select-option"
            :role="isListbox ? 'option' : undefined"
            :tabindex="isListbox ? -1 : undefined"
            :aria-selected="isListbox ? (checkIfSelected(item) ? 'true' : 'false') : undefined"
            :aria-disabled="item.disabled ? 'true' : undefined"
            @click.capture="handleRowClick(item, index, $event)"
          >
            <slot
              :name="props.uniqueSlots ? `item-${item.value}` : 'item'"
              :item="item"
              :selected="checkIfSelected(item)"
            >
              <app-select-item
                :divider="props.divider"
                :item="item"
                :selected="checkIfSelected(item)"
              />
            </slot>
          </div>
        </template>
        <div
          v-if="props.filter && filteredItems.length === 0 && !props.loading"
          class="app-select-empty"
          data-test-id="app-select-empty"
        >
          {{ resolvedEmptyText }}
        </div>
      </slot>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.app-select {
  display: inline-flex;

  .block {
    width: 100%;
  }

  &-trigger-wrapper {
    display: inline-flex;
  }

  &-right-icon {
    color: var(--g-neutral-2);
    font-size: 14px;
    transition: rotate 0.2s ease;
    translate: 0 1px;

    &.open {
      rotate: 180deg;
      translate: 0 0;
    }
  }

  &.block {
    width: 100%;
  }

  &-items {
    z-index: var(--z-layer-floating);
    width: 100%;
    padding: 12px;
    border-radius: 16px;
    background-color: var(--g-neutral-8);
    box-shadow: 0 0 1.8px 0 rgb(0 0 0 / 10%), 0 3.2px 7.2px -1px rgb(0 0 0 / 13%);
    overflow-y: auto;
    scrollbar-color: var(--g-neutral-5) transparent;
    scrollbar-width: thin;

    &:has(.app-select-filter) {
      scroll-padding-top: 64px;
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: var(--g-neutral-5);
    }
  }

  &-option {
    border-radius: 10px;

    &:last-child .app-select-item {
      border-bottom-color: transparent;
    }

    &:focus-visible {
      outline: 2px solid var(--a-lilac);
      outline-offset: -2px;
    }
  }

  &-empty {
    padding: 8px 12px;
    color: var(--g-neutral-3);
    font-size: 14px;
    text-align: center;
  }

  &-filter {
    margin-bottom: 8px;

    &:not(.mobile) {
      position: sticky;
      z-index: 1;
      top: -12px;
      margin: -12px 0 0;
      padding: 12px 0 8px;
      background-color: var(--g-neutral-8);
    }

    &.mobile {
      position: sticky;
      z-index: 1;
      top: -4px;
      margin: 0 -4px 8px;
      padding: 16px 20px;
      background-color: var(--g-neutral-8);
    }
  }
}

:deep(.app-select-input) {
  &:not(.disabled) {
    .app-input-body,
    .app-input-field {
      cursor: pointer;
    }

    .app-input-field {
      caret-color: transparent;
    }

    .app-input-body {
      &:hover {
        --outline-color: var(--g-neutral-3);
      }
    }

    &.open .app-input-body {
      --outline-color: var(--g-neutral-5);
    }
  }
}
</style>
